import { AdditiveBlending, BufferAttribute, BufferGeometry, Points, ShaderMaterial } from "three";
import type { AtmosphereUniforms } from "../atmosphere.ts";
import { roleUniforms } from "../style/roles.ts";
import { assemble } from "./chunks.ts";

/**
 * Partículas da referência "Mintlit" (Fase 13, G6), num desenho só e sem CPU por quadro (tudo no
 * vértice, pelo relógio da atmosfera):
 *
 * - **poeira**: pontos suaves que flutuam num cubo em volta da câmera (repetido pelo mundo), na cor da
 *   névoa; uma fração cintila no acento do mundo (a classe de quem joga);
 * - **anel**: brilhos que sobem do anel do jogador local (`uRingLight`) e somem com a altura, na cor dele.
 *
 * Aditivas, sem escrever profundidade; somem perto da câmera e longe (a névoa as engole).
 */

/** Quantas de cada camada. */
export const MOTES = { dust: 260, ring: 48 } as const;
/** Lado do cubo da poeira em volta da câmera (m) e a altura dele acima do chão da câmera. */
const SPAN = 36;

const vertexShader = /* glsl */ `
${assemble("vertex", ["atmo"])}
uniform vec4 uRingLight;
uniform float uPixelRatio;
uniform float uMoteScale;
// x, z: semente no cubo (0..1); y: altura (0..1); w: semente da fase. kind: 0 poeira, 1 brilho da
// poeira (acento), 2 anel.
in vec4 seed;
in float kind;
out float vAlpha;
out float vKind;
void main() {
  float t = uTime;
  vec3 world;
  float size;
  float alpha;
  if (kind > 1.5) {
    // Sobe do anel em 1,6–2,6 s, girando devagar, e some no alto.
    float life = 1.6 + seed.w;
    float k = fract(t / life + seed.x);
    float a = seed.z * 6.2831 + t * (0.3 + seed.y * 0.4);
    float r = uRingLight.w * (0.85 + 0.3 * seed.y) * (1.0 - k * 0.35);
    world = uRingLight.xyz + vec3(cos(a) * r, 0.05 + k * k * 1.7, sin(a) * r);
    size = 0.035 * (1.0 - k * 0.6);
    alpha = step(0.001, uRingLight.w) * smoothstep(0.0, 0.12, k) * (1.0 - smoothstep(0.55, 1.0, k));
  } else {
    // Cubo em volta da câmera (posição módulo o lado), com deriva lenta e um balanço.
    vec3 drift = vec3(0.21, 0.05, 0.13) * t * (0.4 + seed.w);
    vec3 cell = vec3(seed.x, seed.y, seed.z) * ${SPAN.toFixed(1)} + drift;
    vec3 rel = mod(cell - cameraPosition + ${(SPAN / 2).toFixed(1)}, ${SPAN.toFixed(1)}) - ${(SPAN / 2).toFixed(1)};
    rel.y = mod(seed.y * 9.0 + drift.y + 3.0, 9.0) - 1.5 + sin(t * 0.7 + seed.w * 9.0) * 0.25;
    world = vec3(cameraPosition.x, cameraPosition.y - 2.0, cameraPosition.z) + rel;
    size = kind > 0.5 ? 0.05 : 0.03 + seed.w * 0.025;
    float twinkle = kind > 0.5 ? 0.45 + 0.55 * sin(t * (1.5 + seed.w * 2.0) + seed.x * 40.0) : 1.0;
    alpha = (kind > 0.5 ? 1.0 : 0.55) * max(twinkle, 0.0);
  }
  vec4 view = viewMatrix * vec4(world, 1.0);
  float dist = -view.z;
  alpha *= smoothstep(0.6, 2.0, dist) * (1.0 - smoothstep(14.0, ${(SPAN / 2).toFixed(1)}, dist)) * (1.0 - uNight * 0.5);
  vAlpha = alpha;
  vKind = kind;
  gl_Position = projectionMatrix * view;
  gl_PointSize = max(size * projectionMatrix[1][1] * uMoteScale / max(dist, 0.1) * uPixelRatio, 1.0);
}
`;

const fragmentShader = /* glsl */ `
${assemble("fragment", ["atmo"])}
uniform vec4 uStyleAccent;
uniform vec3 uRingColor;
in float vAlpha;
in float vKind;
void main() {
  vec2 p = gl_PointCoord * 2.0 - 1.0;
  float r2 = dot(p, p);
  if (r2 > 1.0 || vAlpha <= 0.002) discard;
  float core = exp(-r2 * 4.0);
  vec3 color = vKind > 1.5 ? uRingColor * 2.2 : vKind > 0.5 ? mix(uStyleAccent.rgb, vec3(1.0), 0.3) * 2.5 : mix(uFogColor, uLightColor, 0.4) * 0.6;
  gl_FragColor = vec4(color * core * vAlpha, 1.0);
  #include <colorspace_fragment>
}
`;

/** As partículas (um `Points`, fora da sombra do sol, sem descarte pelo frustum: a posição é do shader). */
export function createMotes(atmosphere: AtmosphereUniforms): Points {
  const total = MOTES.dust + MOTES.ring;
  const seed = new Float32Array(total * 4);
  const kind = new Float32Array(total);
  // Sementes estáveis (um gerador linear simples: nada de Math.random, as capturas se repetem).
  let s = 0x2f6b9a1d;
  const next = () => ((s = (Math.imul(s, 1664525) + 1013904223) >>> 0) / 4294967296);
  for (let i = 0; i < total; i++) {
    for (let k = 0; k < 4; k++) seed[i * 4 + k] = next();
    kind[i] = i >= MOTES.dust ? 2 : next() < 0.12 ? 1 : 0;
  }
  const geometry = new BufferGeometry();
  // A posição não é usada (o shader a calcula), mas o three pede o atributo para contar os pontos.
  geometry.setAttribute("position", new BufferAttribute(new Float32Array(total * 3), 3));
  geometry.setAttribute("seed", new BufferAttribute(seed, 4));
  geometry.setAttribute("kind", new BufferAttribute(kind, 1));
  const material = new ShaderMaterial({
    name: "particulas",
    uniforms: { ...atmosphere, ...roleUniforms("vegetacao"), uMoteScale: { value: 540 } },
    vertexShader,
    fragmentShader,
    transparent: true,
    depthWrite: false,
    blending: AdditiveBlending,
  });
  const points = new Points(geometry, material);
  points.name = "particulas";
  points.frustumCulled = false;
  points.renderOrder = 5;
  points.userData["noShadow"] = true;
  return points;
}
